import { CRITERION_LABELS, type CriterionKey } from '../types';
import { bandByKey, BANDS, type BandKey } from '../theme/bands';

export interface RadarAxis {
  criterion: CriterionKey;
  level: number; // 1–5 band level (fractional allowed for cohort aggregates)
  band: BandKey;
}

const SIZE = 400;
const C = SIZE / 2;
const MAX_R = SIZE * 0.27;
const LABEL_R = MAX_R + 48;

function point(i: number, n: number, level: number) {
  const a = ((-90 + (i * 360) / n) * Math.PI) / 180;
  const r = (level / 5) * MAX_R;
  return { x: C + r * Math.cos(a), y: C + r * Math.sin(a) };
}

function polygon(levels: number[], n: number) {
  return levels.map((lvl, i) => {
    const p = point(i, n, lvl);
    return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
  }).join(' ');
}

/** Capability radar with a text-equivalent table beneath (WCAG: not colour-only). */
export function Radar({
  axes,
  cohort,
  compact = false,
  dark = false,
  accent = '#00D700',
}: {
  axes: RadarAxis[];
  cohort?: number[]; // optional cohort-average levels (dashed ghost polygon)
  compact?: boolean; // chart only (no ring legend / a11y table) — for embeds
  dark?: boolean; // dark surfaces (Campaign) — flips grid/label/dot colours
  accent?: string;
}) {
  const n = axes.length;
  const levels = axes.map((a) => a.level);

  const gridIn = dark ? '#26262E' : '#E6E6EA';
  const gridOut = dark ? '#3a3a44' : '#C8C8CC';
  const dotFill = dark ? '#0B0B0F' : '#fff';
  const txt = dark ? '#ADAEB4' : '#333544';
  const ringTxt = dark ? '#ADAEB4' : '#75757F';

  return (
    <div>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="block w-full animate-radarIn"
        style={{ overflow: 'visible' }}
        role="img"
        aria-label={`Capability profile radar. ${axes.map((a) => `${CRITERION_LABELS[a.criterion]}: ${bandByKey(a.band).label}`).join('. ')}.`}
      >
        {/* rings */}
        {[1, 2, 3, 4, 5].map((ring) => (
          <polygon
            key={`r${ring}`}
            points={Array.from({ length: n }, (_, i) => {
              const p = point(i, n, ring);
              return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
            }).join(' ')}
            fill="none"
            stroke={ring === 5 ? gridOut : gridIn}
            strokeWidth={1}
          />
        ))}
        {/* axes */}
        {axes.map((a, i) => {
          const edge = point(i, n, 5);
          return <line key={`a${a.criterion}`} x1={C} y1={C} x2={edge.x} y2={edge.y} stroke={gridIn} strokeWidth={1} />;
        })}
        {/* ring level labels (up the top axis) */}
        {[1, 2, 3, 4, 5].map((ring) => {
          const ry = C - (ring / 5) * MAX_R;
          return (
            <text key={`rl${ring}`} x={C + 5} y={ry - 3} fontSize={10} fontWeight={600} fill={ringTxt}
              fontFamily="Inter" letterSpacing="0.04em">
              {BANDS[ring - 1].label}
            </text>
          );
        })}
        {/* cohort ghost (dashed) */}
        {cohort && (
          <polygon points={polygon(cohort, n)} fill="none" stroke={dark ? '#75757F' : '#ADAEB4'}
            strokeWidth={1.5} strokeDasharray="4 4" strokeLinejoin="round" />
        )}
        {/* user polygon */}
        <polygon points={polygon(levels, n)} fill={`${accent}2e`} stroke={accent} strokeWidth={2.5} strokeLinejoin="round" />
        {axes.map((a, i) => {
          const p = point(i, n, a.level);
          return <circle key={`d${a.criterion}`} cx={p.x} cy={p.y} r={4} fill={dotFill} stroke={accent} strokeWidth={2.5} />;
        })}
        {/* axis labels */}
        {axes.map((a, i) => {
          const a0 = ((-90 + (i * 360) / n) * Math.PI) / 180;
          const lx = C + LABEL_R * Math.cos(a0);
          const ly = C + LABEL_R * Math.sin(a0);
          const words = CRITERION_LABELS[a.criterion].split(' ');
          return (
            <text key={`t${a.criterion}`} x={lx} y={ly - 3} textAnchor="middle" fontSize={14} fontWeight={600} fill={txt} fontFamily="Inter" aria-label={CRITERION_LABELS[a.criterion]}>
              {words.map((word, j) => <tspan key={word} x={lx} dy={j === 0 ? 0 : 15}>{word}{j < words.length - 1 ? ' ' : ''}</tspan>)}
            </text>
          );
        })}
      </svg>

      {compact ? null : (
        <table className="mt-4 w-full border-collapse text-[13px]" style={{ color: txt }}>
          <caption className="sr-only">Capability bands by criterion</caption>
          <thead>
            <tr className="text-left" style={{ color: dark ? '#75757F' : '#75757F' }}>
              <th className="border-b py-1 font-semibold" style={{ borderColor: gridIn }}>Criterion</th>
              <th className="border-b py-1 font-semibold" style={{ borderColor: gridIn }}>Band</th>
            </tr>
          </thead>
          <tbody>
            {axes.map((a) => (
              <tr key={a.criterion}>
                <td className="border-b py-1.5" style={{ borderColor: gridIn }}>{CRITERION_LABELS[a.criterion]}</td>
                <td className="border-b py-1.5" style={{ borderColor: gridIn }}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: bandByKey(a.band).fill }} aria-hidden="true" />
                    {bandByKey(a.band).label}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export const bandToLevel = (band: BandKey) => bandByKey(band).level;
export { BANDS };
